import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { RoundCornerIcon } from '../../icons/round-corner-icon';
import { SharpCornerIcon } from '../../icons/sharp-corner-icon';
import { messages } from './messages';

const styles = cssMap({
	borderOptionContainer: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.input'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '24px',
		width: '24px',
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.accent.blue'),
	},
});

export type CornerStyleConfigProps<TOptionValue> = {
	cornerStyleValue: TOptionValue;
	roundCornerValue: TOptionValue;
	squareCornerValue: TOptionValue;
	updateCornerStyle: (cornerStyle: TOptionValue) => void;
};

export const CornerStyleConfig = <TOptionValue extends string>({
	cornerStyleValue,
	updateCornerStyle,
	roundCornerValue,
	squareCornerValue,
}: CornerStyleConfigProps<TOptionValue>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const handleCornerOptionClick = (cornerOption: TOptionValue) => updateCornerStyle(cornerOption);

	return (
		<Inline spread="space-between" alignBlock="center">
			<Text color="color.text">
				<FormattedMessage {...messages.cornerStyle} />
			</Text>
			<Inline space="space.100" alignBlock="center">
				{[
					{
						cornerOption: roundCornerValue,
						IconComponent: RoundCornerIcon,
						label: messages.roundCornerStyleLabel,
					},
					{
						cornerOption: squareCornerValue,
						IconComponent: SharpCornerIcon,
						label: messages.squareCornerStyleLabel,
					},
				].map(({ cornerOption, IconComponent, label }) => {
					return (
						<Tooltip key={cornerOption} content={formatMessage(label)}>
							<Pressable
								xcss={cx(
									styles.borderOptionContainer,
									styles.border,
									cornerStyleValue === cornerOption && styles.selected,
								)}
								onClick={() => handleCornerOptionClick(cornerOption)}
								testId={`${cornerOption}${cornerStyleValue === cornerOption ? '--selected' : ''}`}
							>
								<IconComponent
									color={
										cornerStyleValue === cornerOption
											? token('color.icon.accent.blue')
											: token('color.icon')
									}
									label={formatMessage(label)}
								/>
							</Pressable>
						</Tooltip>
					);
				})}
			</Inline>
		</Inline>
	);
};
